"use client";

import {
	Page,
	PageHeader,
	PageSection,
	Stat,
	StatStrip,
	SURFACE,
} from "@commerce/components/page";
import { Time } from "@commerce/components/time";
import { STATUS_META as ORDER_STATUS } from "@commerce/orders/meta";
import { formatMoney } from "@commerce/shopper/types";
import { useConfirmationAlert } from "@saas/shared/components/ConfirmationAlertProvider";
import { orpc, orpcClient } from "@shared/lib/orpc";
import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { getRouteApi, Link } from "@tanstack/react-router";
import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@ui/components/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@ui/components/dropdown-menu";
import { Field, FieldLabel } from "@ui/components/field";
import { Input } from "@ui/components/input";
import { LinkButton } from "@ui/components/link-button";
import { Skeleton } from "@ui/components/skeleton";
import { Spinner } from "@ui/components/spinner";
import { toast } from "@ui/components/toast";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@ui/components/tooltip";
import { cn } from "@ui/lib";
import {
	ChevronRightIcon,
	EllipsisIcon,
	EyeIcon,
	MapPinIcon,
	MessagesSquareIcon,
	TrashIcon,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import {
	addressLine,
	type CustomerDetail,
	customerName,
	GEO_SOURCE_LABEL,
	MARKETING_META,
	MARKETING_SOURCE_LABEL,
} from "./meta";

const route = getRouteApi(
	"/_saas/app/_org/$organizationSlug/customers/$customerId",
);

export function customerQuery(organizationId: string, customerId: string) {
	return orpc.customers.get.queryOptions({
		input: { organizationId, customerId, revealPhone: false },
	});
}

/**
 * One customer (spec 5.5): identity, saved addresses, consents, their chats and orders, and
 * the privacy actions. The phone stays masked; an admin can reveal it, which is audited.
 */
export function CustomerScreen({
	organizationId,
	customerId,
	isAdmin,
}: {
	organizationId: string;
	customerId: string;
	isAdmin: boolean;
}) {
	const { data: customer } = useSuspenseQuery(
		customerQuery(organizationId, customerId),
	);
	const ids = { organizationId, customerId };

	return (
		<Page>
			<CustomerHeader customer={customer} isAdmin={isAdmin} {...ids} />
			<CustomerStats customer={customer} />

			<div className="grid gap-8 lg:grid-cols-2">
				<IdentitySection
					customer={customer}
					isAdmin={isAdmin}
					{...ids}
				/>
				<ConsentsSection customer={customer} {...ids} />
			</div>

			<AddressesSection customer={customer} {...ids} />

			<div className="grid gap-8 lg:grid-cols-2">
				<OrdersSection orders={customer.recentOrders} />
				<ConversationsSection conversations={customer.conversations} />
			</div>
		</Page>
	);
}

interface CustomerIds {
	organizationId: string;
	customerId: string;
}

/** Writes to this customer's cached detail and the customers list after a change. */
function useCustomerCache({ organizationId, customerId }: CustomerIds) {
	const queryClient = useQueryClient();
	const { queryKey } = customerQuery(organizationId, customerId);
	return {
		set: (next: CustomerDetail) => queryClient.setQueryData(queryKey, next),
		invalidateDetail: () => queryClient.invalidateQueries({ queryKey }),
		invalidateList: () =>
			queryClient.invalidateQueries(
				{ queryKey: orpc.customers.list.key() },
				{ cancelRefetch: false },
			),
	};
}

/** Saves a name, language or marketing change, then refreshes the detail and the list. */
function useUpdateCustomer(ids: CustomerIds) {
	const cache = useCustomerCache(ids);
	return useMutation(
		orpc.customers.update.mutationOptions({
			onSuccess: (next) => {
				cache.set(next);
				void cache.invalidateList();
				toast.add({ type: "success", title: "Customer updated" });
			},
			onError: (error) =>
				toast.add({
					type: "error",
					title: "Couldn't update the customer",
					description: error.message,
				}),
		}),
	);
}

function errorText(error: unknown) {
	return error instanceof Error ? error.message : undefined;
}

function CustomerHeader({
	customer,
	isAdmin,
	organizationId,
	customerId,
}: CustomerIds & { customer: CustomerDetail; isAdmin: boolean }) {
	const { organizationSlug } = route.useParams();
	const latestChat = customer.conversations[0];
	const name = customerName(customer);
	const marketing = MARKETING_META[customer.marketing];

	return (
		<PageHeader
			back={{
				label: "Customers",
				to: "/app/$organizationSlug/customers",
				params: { organizationSlug },
			}}
			title={<span dir="auto">{name}</span>}
			meta={
				<>
					{customer.linked && customer.retailerCustomerId ? (
						<Badge variant="outline">
							Linked to account{" "}
							<span className="font-mono">
								{customer.retailerCustomerId}
							</span>
						</Badge>
					) : null}
					{customer.language ? (
						<Badge variant="secondary">{customer.language}</Badge>
					) : null}
					<Badge variant={marketing.variant}>{marketing.label}</Badge>
				</>
			}
			actions={
				<>
					{latestChat ? (
						<LinkButton
							variant="outline"
							to="/app/$organizationSlug/conversations"
							params={{ organizationSlug }}
							search={{ id: latestChat.id }}
						>
							<MessagesSquareIcon data-icon="inline-start" />
							Open latest chat
						</LinkButton>
					) : null}
					<CustomerActions
						customer={customer}
						isAdmin={isAdmin}
						organizationId={organizationId}
						customerId={customerId}
					/>
				</>
			}
		/>
	);
}

/** The "more" menu: edit, export and, for admins, delete everything about the customer. */
function CustomerActions({
	customer,
	isAdmin,
	organizationId,
	customerId,
}: CustomerIds & { customer: CustomerDetail; isAdmin: boolean }) {
	const { organizationSlug } = route.useParams();
	const navigate = route.useNavigate();
	const { confirm } = useConfirmationAlert();
	const cache = useCustomerCache({ organizationId, customerId });
	const update = useUpdateCustomer({ organizationId, customerId });
	const deletion = useMutation(
		orpc.privacy.deletions.create.mutationOptions(),
	);
	const [editing, setEditing] = useState(false);
	const name = customerName(customer);

	const exportData = () => {
		const blob = new Blob([JSON.stringify(customer, null, 2)], {
			type: "application/json",
		});
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `customer-${customer.id}.json`;
		link.click();
		URL.revokeObjectURL(url);
		toast.add({ type: "success", title: "Customer data downloaded" });
	};

	const deleteCustomer = () =>
		confirm({
			title: `Delete everything about ${name}?`,
			message:
				"Their chats, messages, addresses, calls and consents are erased, and their past orders lose name, phone and address. This cannot be undone.",
			confirmLabel: "Delete this customer",
			destructive: true,
			onConfirm: async () => {
				try {
					await deletion.mutateAsync({
						organizationId,
						externalShopperId: customer.externalShopperId,
					});
					toast.add({
						type: "success",
						title: "Deletion started",
						description:
							"It finishes in a few minutes. Settings › Shopper data shows its progress.",
					});
					await cache.invalidateList();
					await navigate({
						to: "/app/$organizationSlug/customers",
						params: { organizationSlug },
					});
				} catch (error) {
					toast.add({
						type: "error",
						title: "Couldn't delete the customer",
						description:
							errorText(error) ??
							"Nothing was deleted. Try again.",
					});
				}
			},
		});

	return (
		<>
			<DropdownMenu>
				<DropdownMenuTrigger
					render={
						<Button
							variant="outline"
							size="icon"
							aria-label={`More actions for ${name}`}
						>
							<EllipsisIcon />
						</Button>
					}
				/>
				<DropdownMenuContent align="end" className="w-56">
					<DropdownMenuGroup>
						<DropdownMenuItem onClick={() => setEditing(true)}>
							Edit name and language
						</DropdownMenuItem>
						<DropdownMenuItem onClick={exportData}>
							Export data
						</DropdownMenuItem>
					</DropdownMenuGroup>
					{isAdmin ? (
						<>
							<DropdownMenuSeparator />
							<DropdownMenuItem
								variant="destructive"
								onClick={deleteCustomer}
							>
								Delete this customer
							</DropdownMenuItem>
						</>
					) : null}
				</DropdownMenuContent>
			</DropdownMenu>
			<EditDialog
				open={editing}
				onOpenChange={setEditing}
				customer={customer}
				pending={update.isPending}
				onSave={(patch) =>
					update.mutate(
						{ organizationId, customerId, ...patch },
						{ onSuccess: () => setEditing(false) },
					)
				}
			/>
		</>
	);
}

function CustomerStats({ customer }: { customer: CustomerDetail }) {
	return (
		<StatStrip>
			<Stat
				label="Orders"
				value={customer.orders.toLocaleString("en-US")}
			/>
			<Stat
				label="Spent"
				value={
					customer.spent && customer.currency
						? formatMoney(customer.spent, customer.currency)
						: "-"
				}
			/>
			<Stat
				label="Last order"
				value={
					customer.lastOrderAt ? (
						<Time
							at={new Date(customer.lastOrderAt)}
							as="relative"
							label="Last order"
						/>
					) : (
						"Never"
					)
				}
			/>
			<Stat
				label="Conversations"
				value={customer.conversationCount.toLocaleString("en-US")}
			/>
		</StatStrip>
	);
}

function IdentitySection({
	customer,
	isAdmin,
	...ids
}: CustomerIds & { customer: CustomerDetail; isAdmin: boolean }) {
	return (
		<PageSection title="Identity">
			<dl className={cn(SURFACE, "divide-y divide-border/70 text-sm")}>
				<Row label="WhatsApp name">
					<span dir="auto">{customer.profileName ?? "-"}</span>
				</Row>
				<Row label="Username">
					{customer.username ? (
						<span className="font-mono text-xs">
							@{customer.username}
						</span>
					) : (
						"-"
					)}
				</Row>
				<Row label="Phone">
					<PhoneNumber
						phone={customer.phone}
						canReveal={isAdmin}
						{...ids}
					/>
				</Row>
				<Row label="Shopper id">
					<span className="truncate font-mono text-xs">
						{customer.externalShopperId}
					</span>
				</Row>
				<Row label="Account with you">
					{customer.retailerCustomerId ? (
						<RetailerAccount
							retailerCustomerId={customer.retailerCustomerId}
							{...ids}
						/>
					) : (
						<span className="text-muted-foreground">Guest</span>
					)}
				</Row>
			</dl>
		</PageSection>
	);
}

/** The masked phone, with a button for admins that asks the server (and its audit log) for it. */
function PhoneNumber({
	phone,
	canReveal,
	organizationId,
	customerId,
}: CustomerIds & { phone: string | null; canReveal: boolean }) {
	const queryClient = useQueryClient();
	const [revealed, setRevealed] = useState<string | null>(null);
	const reveal = useMutation({
		// The full number is never cached: each reveal asks the server, which audits it.
		mutationFn: () =>
			orpcClient.customers.get({
				organizationId,
				customerId,
				revealPhone: true,
			}),
		onSuccess: (data) => {
			setRevealed(data.phone);
			// The reveal is a new entry in the organization's audit log.
			void queryClient.invalidateQueries({
				queryKey: orpc.audit.organization.list.key(),
			});
		},
		onError: () =>
			toast.add({
				type: "error",
				title: "Couldn't show the number",
				description: "Try again in a moment.",
			}),
	});

	return (
		<span className="flex items-center gap-2">
			<span className="font-mono text-xs">
				{revealed ?? phone ?? "-"}
			</span>
			{canReveal && phone && !revealed ? (
				<Tooltip>
					<TooltipTrigger
						render={
							<Button
								variant="ghost"
								size="icon-xs"
								onClick={() => reveal.mutate()}
								disabled={reveal.isPending}
								aria-label="Show the full number"
							/>
						}
					>
						{reveal.isPending ? <Spinner /> : <EyeIcon />}
					</TooltipTrigger>
					<TooltipContent>
						Show the full number (recorded in the audit log)
					</TooltipContent>
				</Tooltip>
			) : null}
		</span>
	);
}

/** The retailer account this customer is linked to, and the button that forgets the link. */
function RetailerAccount({
	retailerCustomerId,
	organizationId,
	customerId,
}: CustomerIds & { retailerCustomerId: string }) {
	const { confirm } = useConfirmationAlert();
	const cache = useCustomerCache({ organizationId, customerId });
	const unlink = useMutation(orpc.customers.unlink.mutationOptions());

	const forgetLink = () =>
		confirm({
			title: `Forget the link to account ${retailerCustomerId}?`,
			message:
				"Their next order is matched to an account again, or placed as a guest.",
			confirmLabel: "Forget the link",
			destructive: true,
			onConfirm: async () => {
				try {
					cache.set(
						await unlink.mutateAsync({
							organizationId,
							customerId,
						}),
					);
					toast.add({ type: "success", title: "Link forgotten" });
				} catch (error) {
					toast.add({
						type: "error",
						title: "Couldn't forget the link",
						description: errorText(error),
					});
				}
			},
		});

	return (
		<span className="flex items-center gap-2">
			<span className="font-mono text-xs">{retailerCustomerId}</span>
			<Button
				variant="ghost"
				size="xs"
				disabled={unlink.isPending}
				onClick={forgetLink}
			>
				Unlink
			</Button>
		</span>
	);
}

function ConsentsSection({
	customer,
	...ids
}: CustomerIds & { customer: CustomerDetail }) {
	const update = useUpdateCustomer(ids);
	const marketing = MARKETING_META[customer.marketing];

	return (
		<PageSection title="Consents">
			<dl className={cn(SURFACE, "divide-y divide-border/70 text-sm")}>
				<Row label="Marketing">
					<div className="flex flex-col items-end gap-1">
						<div className="flex items-center gap-2">
							<Badge variant={marketing.variant}>
								{marketing.label}
							</Badge>
							{customer.marketing === "opted_out" ? null : (
								<Button
									size="sm"
									variant="outline"
									disabled={update.isPending}
									onClick={() =>
										update.mutate({
											...ids,
											marketing: "opted_out",
										})
									}
								>
									Record an opt-out
								</Button>
							)}
						</div>
						{customer.marketingChangedAt ? (
							<span className="text-muted-foreground text-xs">
								<Time
									at={new Date(customer.marketingChangedAt)}
									label="Changed"
								/>
								{customer.marketingSource
									? ` · ${MARKETING_SOURCE_LABEL[customer.marketingSource] ?? customer.marketingSource}`
									: ""}
							</span>
						) : null}
					</div>
				</Row>
				<Row label="Remember preferences">
					{memoryLabel(customer.memoryAllowed)}
				</Row>
				<Row label="Privacy notice">
					{customer.privacyNoticeAt ? (
						<Time
							at={new Date(customer.privacyNoticeAt)}
							label="Shown"
						/>
					) : (
						"Not shown yet"
					)}
				</Row>
			</dl>
		</PageSection>
	);
}

function memoryLabel(allowed: boolean | null) {
	if (allowed === null) {
		return "Not asked";
	}
	return allowed ? "Allowed" : "Declined";
}

function AddressesSection({
	customer,
	...ids
}: CustomerIds & { customer: CustomerDetail }) {
	return (
		<PageSection title={`Addresses (${customer.addresses.length})`}>
			{customer.addresses.length === 0 ? (
				<p className="text-muted-foreground text-sm">
					No saved addresses. They are saved at checkout.
				</p>
			) : (
				<ul className={cn(SURFACE, "divide-y divide-border/70")}>
					{customer.addresses.map((address) => (
						<AddressRow
							key={address.id}
							address={address}
							{...ids}
						/>
					))}
				</ul>
			)}
		</PageSection>
	);
}

function AddressRow({
	address,
	organizationId,
	customerId,
}: CustomerIds & { address: CustomerDetail["addresses"][number] }) {
	const { confirm } = useConfirmationAlert();
	const cache = useCustomerCache({ organizationId, customerId });
	const deleteAddress = useMutation(
		orpc.customers.deleteAddress.mutationOptions(),
	);
	const name = address.label ?? "this address";

	const remove = () =>
		confirm({
			title: `Remove ${name}?`,
			message:
				"The shopper is asked for an address again at their next checkout.",
			confirmLabel: "Remove address",
			destructive: true,
			onConfirm: async () => {
				try {
					await deleteAddress.mutateAsync({
						organizationId,
						customerId,
						addressId: address.id,
					});
					await cache.invalidateDetail();
					toast.add({ type: "success", title: "Address removed" });
				} catch (error) {
					toast.add({
						type: "error",
						title: "Couldn't remove the address",
						description: errorText(error),
					});
				}
			},
		});

	return (
		<li className="flex items-center gap-3 px-4 py-3 md:px-5">
			<MapPinIcon
				className="size-4 shrink-0 text-muted-foreground"
				aria-hidden
			/>
			<div className="flex min-w-0 flex-1 flex-col gap-0.5">
				<span className="flex flex-wrap items-center gap-2 text-sm">
					{address.label ? (
						<span className="font-medium">{address.label}</span>
					) : null}
					<span dir="auto" className="text-left">
						{addressLine(address) || "No details"}
					</span>
				</span>
				<span className="text-muted-foreground text-xs">
					{GEO_SOURCE_LABEL[address.geoSource] ?? address.geoSource}
					{address.landmark ? ` · ${address.landmark}` : ""}
					{address.lastUsedAt ? (
						<>
							{" · used "}
							<Time
								at={new Date(address.lastUsedAt)}
								label="Last used"
							/>
						</>
					) : null}
				</span>
			</div>
			{address.latitude !== null && address.longitude !== null ? (
				<a
					href={`https://www.google.com/maps/search/?api=1&query=${address.latitude},${address.longitude}`}
					target="_blank"
					rel="noreferrer"
					className="shrink-0 text-sm underline underline-offset-4"
				>
					Map
				</a>
			) : null}
			<Tooltip>
				<TooltipTrigger
					render={
						<Button
							variant="ghost"
							size="icon-sm"
							aria-label={`Remove ${name}`}
							onClick={remove}
						/>
					}
				>
					<TrashIcon />
				</TooltipTrigger>
				<TooltipContent>Remove address</TooltipContent>
			</Tooltip>
		</li>
	);
}

function OrdersSection({ orders }: { orders: CustomerDetail["recentOrders"] }) {
	const { organizationSlug } = route.useParams();
	return (
		<PageSection title="Orders">
			{orders.length === 0 ? (
				<p className="text-muted-foreground text-sm">No orders yet.</p>
			) : (
				<ul className={cn(SURFACE, "divide-y divide-border/70")}>
					{orders.map((order) => {
						const status =
							ORDER_STATUS[
								order.status as keyof typeof ORDER_STATUS
							];
						return (
							<li key={order.id}>
								<Link
									to="/app/$organizationSlug/orders"
									params={{ organizationSlug }}
									search={{ id: order.id }}
									className="flex items-center gap-3 px-4 py-2.5 text-sm outline-none hover:bg-muted/50 focus-visible:bg-muted/50"
								>
									<span className="w-14 shrink-0 font-medium tabular">
										{order.number ?? order.externalOrderId}
									</span>
									{status ? (
										<Badge variant={status.variant}>
											{status.label}
										</Badge>
									) : null}
									<Time
										at={new Date(order.placedAt)}
										className="min-w-0 flex-1 truncate text-muted-foreground text-xs"
									/>
									<span className="shrink-0 tabular">
										{formatMoney(
											order.total,
											order.currency,
										)}
									</span>
									<ChevronRightIcon
										className="size-4 shrink-0 text-muted-foreground"
										aria-hidden
									/>
								</Link>
							</li>
						);
					})}
				</ul>
			)}
		</PageSection>
	);
}

const CHANNEL_LABEL: Record<string, string> = {
	WHATSAPP: "WhatsApp",
	WEB: "Web chat",
};

function ConversationsSection({
	conversations,
}: {
	conversations: CustomerDetail["conversations"];
}) {
	const { organizationSlug } = route.useParams();
	return (
		<PageSection title="Conversations">
			{conversations.length === 0 ? (
				<p className="text-muted-foreground text-sm">
					No conversations.
				</p>
			) : (
				<ul className={cn(SURFACE, "divide-y divide-border/70")}>
					{conversations.map((conversation) => (
						<li key={conversation.id}>
							<Link
								to="/app/$organizationSlug/conversations"
								params={{ organizationSlug }}
								search={{ id: conversation.id }}
								className="flex items-center gap-3 px-4 py-2.5 text-sm outline-none hover:bg-muted/50 focus-visible:bg-muted/50"
							>
								<MessagesSquareIcon
									className="size-4 shrink-0 text-muted-foreground"
									aria-hidden
								/>
								<span className="min-w-0 flex-1 truncate">
									{CHANNEL_LABEL[conversation.channel] ??
										conversation.channel}
								</span>
								{conversation.lastMessageAt ? (
									<Time
										at={
											new Date(conversation.lastMessageAt)
										}
										as="relative"
										label="Last message"
										className="shrink-0 text-muted-foreground text-xs"
									/>
								) : null}
								<ChevronRightIcon
									className="size-4 shrink-0 text-muted-foreground"
									aria-hidden
								/>
							</Link>
						</li>
					))}
				</ul>
			)}
		</PageSection>
	);
}

function Row({ label, children }: { label: string; children: ReactNode }) {
	return (
		<div className="flex items-center justify-between gap-4 px-4 py-2.5 md:px-5">
			<dt className="shrink-0 text-muted-foreground">{label}</dt>
			<dd className="flex min-w-0 justify-end text-right">{children}</dd>
		</div>
	);
}

function EditDialog({
	open,
	onOpenChange,
	customer,
	pending,
	onSave,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	customer: CustomerDetail;
	pending: boolean;
	onSave: (patch: { name: string | null; language: string | null }) => void;
}) {
	const [name, setName] = useState(customer.name ?? "");
	const [language, setLanguage] = useState(customer.language ?? "");
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Edit customer</DialogTitle>
				</DialogHeader>
				<Field>
					<FieldLabel htmlFor="customer-name">
						Name for orders
					</FieldLabel>
					<Input
						id="customer-name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={customer.profileName ?? "Rami Khoury"}
						maxLength={100}
						dir="auto"
					/>
				</Field>
				<Field>
					<FieldLabel htmlFor="customer-language">
						Language
					</FieldLabel>
					<Input
						id="customer-language"
						value={language}
						onChange={(e) => setLanguage(e.target.value)}
						placeholder="Arabizi"
						maxLength={20}
					/>
				</Field>
				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
					>
						Cancel
					</Button>
					<Button
						onClick={() =>
							onSave({
								name: name.trim() || null,
								language: language.trim() || null,
							})
						}
						disabled={pending}
					>
						{pending ? <Spinner data-icon="inline-start" /> : null}
						Save
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

export function CustomerSkeleton() {
	return (
		<Page>
			<div className="flex flex-col gap-3">
				<Skeleton className="h-7 w-28" />
				<Skeleton className="h-8 w-56" />
				<Skeleton className="h-5 w-40" />
			</div>
			<Skeleton className="h-24 w-full rounded-3xl" />
			<div className="grid gap-8 lg:grid-cols-2">
				<Skeleton className="h-56 w-full rounded-3xl" />
				<Skeleton className="h-56 w-full rounded-3xl" />
			</div>
		</Page>
	);
}
